import { Controller } from "@hotwired/stimulus";

/**
 * Lightbox Controller
 * Manages image gallery with lightbox-style transitions and touch interactions
 */
export default class extends Controller {
  static targets = [
    "modal",
    "image",
    "caption",
    "counter",
    "spinner",
    "error",
    "previous",
    "next",
  ];
  static values = {
    index: Number,
    total: Number,
    sources: Array,
    captions: Array,
  };
  static classes = ["transitioning"];

  // Lifecycle methods
  connect() {
    this.backdrop = null;
    this.bindEvents();

    this.touchStartX = null;
    this.isTransitioning = false;
  }

  disconnect() {
    // Clear timeout on disconnect to prevent memory leaks
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.removeBackdrop();
    document.body.classList.remove("modal-open");
    this.unbindEvents();
  }

  // Event binding
  bindEvents() {
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    document.addEventListener("keyup", this.handleKeyup);
    this.imageTarget.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.imageTarget.addEventListener("touchend", this.handleTouchEnd);
  }

  unbindEvents() {
    document.removeEventListener("keyup", this.handleKeyup);
    this.imageTarget.removeEventListener("touchstart", this.handleTouchStart);
    this.imageTarget.removeEventListener("touchend", this.handleTouchEnd);
  }

  // Simple touch handling
  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
  }

  handleTouchEnd(event) {
    if (!this.touchStartX) return;

    const touchEndX = event.changedTouches[0].clientX;
    const diff = touchEndX - this.touchStartX;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0 && this.canNavigatePrevious) {
        this.previous();
      } else if (diff < 0 && this.canNavigateNext) {
        this.next();
      }
    }

    this.touchStartX = null;
  }

  // Modal actions
  show(event) {
    event.preventDefault();
    const clickedImage = event.currentTarget;
    const imageUrl = clickedImage.dataset.lightboxUrl;

    // Build gallery from clicked image gallery
    // This allows for multiple galleries on the same page, but only one lightbox component
    this.loadGalleryImages(clickedImage.dataset.lightboxGallery);
    const index = this.sourcesValue.indexOf(imageUrl);

    if (index === -1) {
      console.warn("Lightbox: clicked image URL not found in gallery sources", {
        imageUrl,
        sources: this.sourcesValue,
      });
      return;
    }

    this.indexValue = index;
    this.showLoading();
    this.updateDisplay();
    this.openModal();
  }

  hide() {
    this.closeModal();
    this.hideError();
  }

  // Close when clicking the empty area around the image
  backdropClose(event) {
    if (event.target === event.currentTarget) {
      this.hide();
    }
  }

  // Manual modal management (replaces Bootstrap Modal JS)
  openModal() {
    this.modalTarget.classList.add("show");
    this.modalTarget.style.display = "block";
    document.body.classList.add("modal-open");
    this.addBackdrop();
  }

  closeModal() {
    this.modalTarget.classList.remove("show");
    this.modalTarget.style.display = "none";
    document.body.classList.remove("modal-open");
    this.removeBackdrop();
  }

  addBackdrop() {
    if (this.backdrop) return;
    this.backdrop = document.createElement("div");
    this.backdrop.className = "modal-backdrop fade show";
    document.body.appendChild(this.backdrop);
  }

  removeBackdrop() {
    this.backdrop?.remove();
    this.backdrop = null;
  }

  // Navigation actions
  async next() {
    if (this.canNavigateNext && !this.isTransitioning) {
      await this.transitionToImage("next");
      this.updateNavigationButtons();
    }
  }

  async previous() {
    if (this.canNavigatePrevious && !this.isTransitioning) {
      await this.transitionToImage("previous");
      this.updateNavigationButtons();
    }
  }

  // Loading state management
  showLoading() {
    this.hideError();

    this.imageTarget.classList.add("opacity-0");

    // Clear any existing loading timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    // Only show spinner after a delay
    this.loadingTimeout = setTimeout(() => {
      if (this.imageTarget.classList.contains("opacity-0")) {
        this.spinnerTarget.classList.remove("d-none");
        this.spinnerTarget.classList.remove("fade-out");
      }
    }, 500); // Adjust this delay as needed
  }

  hideLoading() {
    // Clear the timeout if image loads before spinner shows
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }

    this.spinnerTarget.classList.add("fade-out");
    this.imageTarget.classList.remove("opacity-0");
    setTimeout(() => {
      this.spinnerTarget.classList.add("d-none");
    }, 150);
  }

  hideError() {
    this.errorTarget.classList.add("d-none");
  }

  // Keyboard navigation
  handleKeyup(event) {
    if (!this.isModalVisible) return;

    const actions = {
      ArrowLeft: () => this.previous(),
      ArrowRight: () => this.next(),
      Escape: () => this.hide(),
    };

    actions[event.key]?.();
  }

  // Image loading
  loadGalleryImages(gallery) {
    if (this.gallery === gallery) return;
    this.gallery = gallery;

    const images = document.querySelectorAll(
      `[data-lightbox-gallery="${gallery}"]`,
    );
    //console.log({ imageTarget: this.imageTarget.dataset, gallery, images })

    this.sourcesValue = Array.from(images).map(
      (img) => img.dataset.lightboxUrl,
    );

    //this.captionsValue = Array.from(images).map(img => img.getAttribute("data-lightbox-caption"))
    this.totalValue = this.sourcesValue.length;
  }

  handleImageLoad() {
    this.hideLoading();
  }

  handleImageError() {
    this.spinnerTarget.classList.add("d-none");
    this.errorTarget.classList.remove("d-none");
    this.imageTarget.classList.add("opacity-0");
  }

  // Transitions
  async transitionToImage(direction) {
    if (this.isTransitioning) return;

    this.startTransition();
    try {
      this.showLoading();
      await this.animateImageExit(direction);
      this.updateImageIndex(direction);
      this.updateDisplay();
      await this.animateImageEntrance(direction);
    } finally {
      this.endTransition();
    }
  }

  async animateImageExit(direction) {
    this.imageTarget.style.transition = this.transitions.normal;
    this.imageTarget.style.opacity = "0";
    this.imageTarget.style.transform = this.getExitTransform(direction);

    await this.wait(200);
  }

  async animateImageEntrance(direction) {
    this.imageTarget.style.transition = "none";
    this.imageTarget.style.transform = this.getEntranceTransform(direction);
    this.imageTarget.style.opacity = "0";

    // Force reflow
    this.imageTarget.offsetHeight;

    this.imageTarget.style.transition = this.transitions.normal;
    this.imageTarget.style.transform = "";
    this.imageTarget.style.opacity = "1";

    await this.wait(200);
  }

  // Display updates
  updateDisplay() {
    this.imageTarget.src = this.sourcesValue[this.indexValue];
    //this.captionTarget.textContent = this.captionsValue[this.indexValue]
    this.counterTarget.textContent = this.counterText;
    this.updateNavigationButtons();

    this.dispatch("change", {
      detail: { index: this.indexValue, total: this.totalValue },
    });
  }

  updateNavigationButtons() {
    this.previousTarget.classList.toggle("disabled", !this.canNavigatePrevious);
    this.nextTarget.classList.toggle("disabled", !this.canNavigateNext);
  }

  // State management
  startTransition() {
    this.isTransitioning = true;
    this.element.classList.add(this.transitioningClass);
  }

  endTransition() {
    this.isTransitioning = false;
    this.element.classList.remove(this.transitioningClass);
  }

  updateImageIndex(direction) {
    this.indexValue += direction === "next" ? 1 : -1;
  }

  // Utility getters and methods
  get transitions() {
    return {
      normal: "transform 0.3s ease-out, opacity 0.3s ease-out",
    };
  }

  get isModalVisible() {
    return this.modalTarget.classList.contains("show");
  }

  get counterText() {
    return `${this.indexValue + 1} / ${this.totalValue}`;
  }

  get canNavigateNext() {
    return this.indexValue < this.totalValue - 1;
  }

  get canNavigatePrevious() {
    return this.indexValue > 0;
  }

  getExitTransform(direction) {
    const pixels = direction === "next" ? -40 : 40;
    return `translate3d(${pixels}px, 0, 0)`;
  }

  getEntranceTransform(direction) {
    const pixels = direction === "next" ? 40 : -40;
    return `translate3d(${pixels}px, 0, 0)`;
  }

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
